import { NavLink } from 'react-router-dom';

import { AuthStatus } from '@/authentication/auth-status';
import { useGame } from '@/hooks/useGame';
import logo from '@/images/light-nacon.png';
import { sandboxCode } from '@/lib/utils';
import type { DashboardItem } from '@/models/dashboardItem';
import type { Game } from '@/models/game';

import { GameImage } from '../gameMiniature';
import {
  Sidebar as ShadSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../ui/sidebar';

interface ProSidebarProps {
  items?: DashboardItem[];
  selectGame?: boolean;
  selectedGame?: Game;
}

const MenuItem = ({
  item,
  parentLink,
}: {
  item: DashboardItem;
  parentLink?: string;
}) => {
  return (
    <SidebarMenuItem key={item.label}>
      <SidebarMenuButton asChild>
        <NavLink
          end
          className={({ isActive }) => (isActive ? 'active' : '')}
          to={parentLink ? `${parentLink}/${item.link}` : item.link}
        >
          {item.icon} {item.label}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const MenuItemWithChildren = ({ item }: { item: DashboardItem }) => {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{item.label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {item.items?.map((_item) => (
            <MenuItem key={_item.label} item={_item} parentLink={item.link} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export const AppSidebar = ({ selectGame, items = [] }: ProSidebarProps) => {
  const game = useGame();
  return (
    <ShadSidebar side="left">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a
                href={`/game/${game.game_code}/${sandboxCode(game.game_code)}/players`}
              >
                <img
                  className="watermark"
                  src={logo}
                  width={24}
                  height={24}
                  alt="Nacon Live"
                />
                <span className="text-lg">Nacon Live</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {selectGame && (
          <SidebarGroup>
            <SidebarGroupContent>
              <NavLink end to="/game">
                <GameImage game={game} />
              </NavLink>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {items.map((item) =>
          item.items?.length ? (
            <MenuItemWithChildren key={item.label} item={item} />
          ) : (
            <SidebarGroup key={item.label}>
              <SidebarGroupContent>
                <SidebarMenu>
                  <MenuItem item={item} />
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ),
        )}
      </SidebarContent>
      <SidebarFooter>
        <AuthStatus />
      </SidebarFooter>
    </ShadSidebar>
  );
};
